import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from "react-router-dom";
import { alertSuccessMessage } from '../../../customComponents/CustomAlertMessage';
import AddFundsModal from "../margin/AddFundsModal";
import { FuturesTradeTransferProvider, useFuturesTradeTransfer } from "../futures_trade/FuturesTradeTransferContext";
import './simple_earn.css';

const SIMPLE_EARN_FAQ = [
    { question: "What is Simple Earn?", answer: "AGCX Simple Earn lets you earn daily rewards on idle digital assets held on AGCX. It offers both flexible and locked options depending on the product." },
    { question: "How is the interest of Simple Earn Flexible Term calculated and distributed?", answer: "On AGCX, flexible rewards are calculated daily based on your subscribed amount and the estimated APR of the selected product, then credited to your AGCX wallet." },
    { question: "How is the interest of Simple Earn Fixed Term calculated and distributed?", answer: "AGCX fixed-term interest is calculated over the locked period and distributed either daily or at the end of the term, depending on the product rules shown on AGCX Earn." },
    {
        question: "How to redeem the crypto you lent?",
        answer: [
            "AGCX Simple Earn Flexible Term allows real-time redemption to your AGCX spot wallet. If many redemptions are processing, there may be a short delay. Interest can keep accruing during the wait.",
            "AGCX Simple Earn Fixed Term may allow early redemption before maturity. Early redemption typically forfeits accrued interest. Principal returns to your AGCX Spot wallet after processing. Check the product page for the last-hour-before-maturity rule."
        ]
    },
    { question: "Guide to Simple Earn (Course)", answer: "Use the AGCX Earn and Simple Earn pages for product rules, APR, and redemption steps. For help, contact support@arabglobal.ae." }
];

const SIMPLE_EARN_MARKET_DATA = [
    {
        id: "usdt",
        name: "USDT",
        icon: "/images/staking_img/usdt_icon.svg",
        apr: "6.78%",
        termsText: "Flexible/Fixed",
        tags: ["VIP", "Event", "Bonus"],
        campaigns: [
            {
                id: "usdt-c0",
                tags: ["Bonus"],
                hasChart: true,
                terms: [
                    { label: "Flexible Term", apr: "6.78%" },
                    { label: "7", apr: "8.50%" },
                    { label: "14", apr: "10.00%" },
                    { label: "30", apr: "12.50%" }
                ],
                defaultTerm: "Flexible Term"
            },
            {
                id: "usdt-c1",
                tags: ["V5-14"],
                terms: [
                    { label: "7", apr: "0.88%" },
                    { label: "30", apr: "1.18%" }
                ],
                defaultTerm: "30"
            },
            {
                id: "usdt-c2",
                tags: ["New Users Exclusive"],
                terms: [
                    { label: "3", apr: "100.00%" }
                ],
                defaultTerm: "3"
            },
            {
                id: "usdt-c3",
                tags: ["Crazy Wednesday", "Bonus"],
                terms: [
                    { label: "14", apr: "6.00%" }
                ],
                defaultTerm: "14"
            }
        ]
    },
    {
        id: "btc",
        name: "BTC",
        icon: "/images/staking_img/btc_icon.svg",
        apr: "10.05%",
        termsText: "Flexible/Fixed",
        tags: ["Bonus"],
        campaigns: [
            {
                id: "btc-c0",
                tags: ["Bonus"],
                terms: [
                    { label: "Flexible Term", apr: "1.50%" },
                    { label: "7", apr: "10.05%" },
                    { label: "14", apr: "2.50%" },
                    { label: "30", apr: "3.80%" }
                ],
                defaultTerm: "7"
            }
        ]
    },
    {
        id: "gt",
        name: "GT",
        icon: "/images/staking_img/agce_icon.svg",
        apr: "0.67%",
        termsText: "Flexible/Fixed",
        tags: ["Event"],
        campaigns: [
            {
                id: "gt-c0",
                tags: [],
                terms: [
                    { label: "Flexible Term", apr: "0.50%" },
                    { label: "7", apr: "0.55%" },
                    { label: "14", apr: "0.66%" },
                    { label: "30", apr: "0.75%" }
                ],
                defaultTerm: "14"
            }
        ]
    },
    {
        id: "eth",
        name: "ETH",
        icon: "/images/staking_img/eth_icon.svg",
        apr: "2.05%",
        termsText: "Flexible/Fixed",
        tags: ["Event"],
        campaigns: [
            {
                id: "eth-c0",
                tags: ["Bonus"],
                terms: [
                    { label: "Flexible Term", apr: "2.05%" },
                    { label: "7", apr: "3.00%" },
                    { label: "14", apr: "4.20%" },
                    { label: "30", apr: "5.50%" }
                ],
                defaultTerm: "Flexible Term"
            }
        ]
    },
    {
        id: "sol",
        name: "SOL",
        icon: "/images/staking_img/sol_icon.svg",
        apr: "4.80%",
        termsText: "Flexible/Fixed",
        tags: ["Bonus"],
        campaigns: [
            {
                id: "sol-c0",
                tags: ["Bonus"],
                terms: [
                    { label: "Flexible Term", apr: "4.80%" },
                    { label: "7", apr: "6.00%" },
                    { label: "14", apr: "7.50%" }
                ],
                defaultTerm: "Flexible Term"
            }
        ]
    },
    {
        id: "xaut",
        name: "XAUT",
        icon: "/images/staking_img/usdt_icon.svg",
        apr: "3.50%",
        termsText: "Flexible/Fixed",
        tags: ["VIP"],
        campaigns: [
            {
                id: "xaut-c0",
                tags: ["VIP"],
                terms: [
                    { label: "Flexible Term", apr: "3.50%" },
                    { label: "30", apr: "6.00%" }
                ],
                defaultTerm: "Flexible Term"
            }
        ]
    },
    {
        id: "kaio",
        name: "KAIO",
        icon: "/images/staking_img/og_icon.svg",
        apr: "12.00%",
        termsText: "Flexible/Fixed",
        tags: ["Bonus"],
        campaigns: [
            {
                id: "kaio-c0",
                tags: ["Bonus"],
                terms: [
                    { label: "Flexible Term", apr: "12.00%" },
                    { label: "7", apr: "15.00%" },
                    { label: "30", apr: "22.00%" }
                ],
                defaultTerm: "Flexible Term"
            }
        ]
    }
];

const SimpleEarn = () => {
    const navigate = useNavigate();
    const { openWalletTransfer } = useFuturesTradeTransfer() || {};
    const [addFundsOpen, setAddFundsOpen] = useState(false);

    const [openFaq, setOpenFaq] = useState(null);
    const [marketSearch, setMarketSearch] = useState("");
    const [marketFilter, setMarketFilter] = useState("All Products");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [expandedRows, setExpandedRows] = useState([]);
    const [selectedTerms, setSelectedTerms] = useState(() => {
        const defaults = {};
        SIMPLE_EARN_MARKET_DATA.forEach(coin => {
            coin.campaigns.forEach(c => {
                defaults[c.id] = c.defaultTerm;
            });
        });
        return defaults;
    });
    const [showAboutModal, setShowAboutModal] = useState(false);
    const [modalTab, setModalTab] = useState("Introduction");
    const [introSubTab, setIntroSubTab] = useState("Strengths");

    const [showSubscribeModal, setShowSubscribeModal] = useState(false);
    const [subCoin, setSubCoin] = useState(null);
    const [subCampaign, setSubCampaign] = useState(null);
    const [subTerm, setSubTerm] = useState(null);
    const [subAmount, setSubAmount] = useState("");
    const [subAutoEarn, setSubAutoEarn] = useState(false);
    const [subRulesTab, setSubRulesTab] = useState("Trading Rules");
    const [showAutoEarnModal, setShowAutoEarnModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);

    useEffect(() => {
        const isAnyModalOpen = showAboutModal || showSubscribeModal || showAutoEarnModal || showInviteModal || addFundsOpen;
        if (isAnyModalOpen) {
            document.body.style.overflow = "hidden";
            document.documentElement.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
            document.documentElement.style.overflow = "";
        };
    }, [showAboutModal, showSubscribeModal, showAutoEarnModal, showInviteModal, addFundsOpen]);
    const openSubscribeModal = (coinId, termLabel) => {
        const coin = SIMPLE_EARN_MARKET_DATA.find(c => c.id === coinId);
        if (!coin) return;

        // Find a campaign that has this term, or use the first one
        let campaign = coin.campaigns.find(camp => camp.terms.some(t => t.label === termLabel));
        if (!campaign) {
            campaign = coin.campaigns[0];
        }

        const termObj = campaign.terms.find(t => t.label === termLabel) || campaign.terms[0];

        setSubCoin(coin);
        setSubCampaign(campaign);
        setSubTerm(termObj.label);
        setSubAmount("");
        setSubAutoEarn(false);
        setSubRulesTab("Trading Rules");
        setShowSubscribeModal(true);
    };

    const toggleFaq = (i) => {
        setOpenFaq((v) => (v === i ? null : i));
    };

    const toggleRow = (id) => {
        setExpandedRows(prev =>
            prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
        );
    };

    const handleSubscribe = (coinName, term, apr) => {
        alertSuccessMessage(`Successfully subscribed to ${coinName} ${term} Simple Earn at ${apr} Est. APR!`);
    };

    const filteredMarketData = SIMPLE_EARN_MARKET_DATA.filter(coin => {
        const matchesSearch = coin.name.toLowerCase().includes(marketSearch.toLowerCase());
        if (marketFilter === "All Products") {
            return matchesSearch;
        }
        return matchesSearch && (coin.tags.includes(marketFilter) || coin.campaigns.some(c => c.tags.includes(marketFilter)));
    });

    return (
        <div className="simple_earn_wrapper">

            <div className="container">
                <div className="launchpad_cnt_block">
                    <div className='launchpad_left_cnt'>
                        <h1>Simple Earn</h1>
                        <p>Earn on idle funds. Join or exit anytime</p>
                        <ul>
                            <li>1.52B<span>Total Lending (USDT)</span></li>
                            <li>6.78%<span>USDT Est. APR</span></li>
                        </ul>
                        <div className='about_Launchpad_btn d-flex gap-3'>
                            {/* <button className='aboutbtn_launchpad'>Log In</button> */}
                            <button className='aboutbtn_launchpad' onClick={() => { setShowAboutModal(true); setModalTab("Introduction"); setIntroSubTab("Strengths"); }}>About Simple Earn</button>
                            <button className='notification_btn' onClick={() => setShowInviteModal(true)}><i className="ri-share-fill"></i></button>
                        </div>

                    </div>

                    <div className='launchpad_right_cnt'>
                        <img src="images/launchpad-img/launchpad_acge.png" alt="launchpad_acge" />
                    </div>

                </div>

                {/* Prudent Trader Section */}
                <div className="prudent_trader_section mt-5 mb-5">
                    <div className="rt_header_wrapper">
                        <div className="rt_header_left">
                            <h2>Recommended</h2>
                            <p>Discover the top flexible yields available on Simple Earn.</p>
                        </div>
                    </div>
                    <div className="prudent_trader_list">
                        {/* Card 1: BTC (7 Days) */}
                        <div className="rt_card">
                            <div className="rt_user">
                                <img src="/images/staking_img/btc_icon.svg" alt="BTC" />
                                <div className="rt_user_info">
                                    <h4>BTC</h4>
                                    <p>7 Days</p>
                                </div>
                            </div>
                            <div className="rt_roi">
                                <p className="roi_label">Est. APR</p>
                                <h3 className="green_text">10.05%</h3>
                            </div>
                            <button className="copy_btn" onClick={() => openSubscribeModal("btc", "7")}>Subscribe</button>
                        </div>

                        {/* Card 2: USDT (Flexible) */}
                        <div className="rt_card">
                            <div className="rt_user">
                                <img src="/images/staking_img/usdt_icon.svg" alt="USDT" />
                                <div className="rt_user_info">
                                    <h4>USDT</h4>
                                    <p>Flexible</p>
                                </div>
                            </div>
                            <div className="rt_roi">
                                <p className="roi_label">Est. APR</p>
                                <h3 className="green_text">6.78%</h3>
                            </div>
                            <button className="copy_btn" onClick={() => openSubscribeModal("usdt", "Flexible Term")}>Subscribe</button>
                        </div>

                        {/* Card 3: ETH (30 Days) */}
                        <div className="rt_card">
                            <div className="rt_user">
                                <img src="/images/staking_img/eth_icon.svg" alt="ETH" />
                                <div className="rt_user_info">
                                    <h4>ETH</h4>
                                    <p>30 Days</p>
                                </div>
                            </div>
                            <div className="rt_roi">
                                <p className="roi_label">Est. APR</p>
                                <h3 className="green_text">5.50%</h3>
                            </div>
                            <button className="copy_btn" onClick={() => openSubscribeModal("eth", "30")}>Subscribe</button>
                        </div>

                        {/* Card 4: GT (14 Days) */}
                        <div className="rt_card">
                            <div className="rt_user">
                                <img src="/images/staking_img/agce_icon.svg" alt="GT" />
                                <div className="rt_user_info">
                                    <h4>GT</h4>
                                    <p>14 Days</p>
                                </div>
                            </div>
                            <div className="rt_roi">
                                <p className="roi_label">Est. APR</p>
                                <h3 className="green_text">0.66%</h3>
                            </div>
                            <button className="copy_btn" onClick={() => openSubscribeModal("gt", "14")}>Subscribe</button>
                        </div>

                        {/* Card 5: SOL (Flexible) */}
                        <div className="rt_card">
                            <div className="rt_user">
                                <img src="/images/staking_img/sol_icon.svg" alt="SOL" />
                                <div className="rt_user_info">
                                    <h4>SOL</h4>
                                    <p>Flexible</p>
                                </div>
                            </div>
                            <div className="rt_roi">
                                <p className="roi_label">Est. APR</p>
                                <h3 className="green_text">4.80%</h3>
                            </div>
                            <button className="copy_btn" onClick={() => openSubscribeModal("sol", "Flexible Term")}>Subscribe</button>
                        </div>

                        {/* Card 6: KAIO (30 Days) */}
                        <div className="rt_card">
                            <div className="rt_user">
                                <img src="/images/staking_img/og_icon.svg" alt="KAIO" />
                                <div className="rt_user_info">
                                    <h4>KAIO</h4>
                                    <p>30 Days</p>
                                </div>
                            </div>
                            <div className="rt_roi">
                                <p className="roi_label">Est. APR</p>
                                <h3 className="green_text">22.00%</h3>
                            </div>
                            <button className="copy_btn" onClick={() => openSubscribeModal("kaio", "30")}>Subscribe</button>
                        </div>

                        {/* Card 7: BTC (Flexible) */}
                        <div className="rt_card">
                            <div className="rt_user">
                                <img src="/images/staking_img/btc_icon.svg" alt="BTC" />
                                <div className="rt_user_info">
                                    <h4>BTC</h4>
                                    <p>Flexible</p>
                                </div>
                            </div>
                            <div className="rt_roi">
                                <p className="roi_label">Est. APR</p>
                                <h3 className="green_text">1.50%</h3>
                            </div>
                            <button className="copy_btn" onClick={() => openSubscribeModal("btc", "Flexible Term")}>Subscribe</button>
                        </div>

                        {/* Card 8: USDT (30 Days) */}
                        <div className="rt_card">
                            <div className="rt_user">
                                <img src="/images/staking_img/usdt_icon.svg" alt="USDT" />
                                <div className="rt_user_info">
                                    <h4>USDT</h4>
                                    <p>30 Days</p>
                                </div>
                            </div>
                            <div className="rt_roi">
                                <p className="roi_label">Est. APR</p>
                                <h3 className="green_text">12.50%</h3>
                            </div>
                            <button className="copy_btn" onClick={() => openSubscribeModal("usdt", "30")}>Subscribe</button>
                        </div>
                    </div>

                    <div className='recommed_btn d-flex align-items-center justify-content-center mt-3'>
                        <button className="more_btn">View More</button>
                    </div>

                </div>

                {/* Market Section */}
                <section className="market-section mt-5 mb-5" aria-labelledby="market-section-title">
                    <div className="market-header d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                        <h2 id="market-section-title" className="market-title m-0">Market</h2>
                        <div className="market-controls d-flex gap-3 align-items-center flex-wrap">
                            {/* Custom Dropdown */}
                            <div className="market-dropdown-wrapper">
                                <button
                                    type="button"
                                    className="market-dropdown-btn d-flex align-items-center justify-content-between"
                                    onClick={() => setDropdownOpen(!dropdownOpen)}
                                >
                                    <span>{marketFilter}</span>
                                    <i className={`ri-arrow-down-s-line ms-2 dropdown-chev ${dropdownOpen ? 'open' : ''}`}></i>
                                </button>
                                {dropdownOpen && (
                                    <ul className="market-dropdown-menu">
                                        {["All Products", "VIP", "Event", "Bonus"].map((filter) => (
                                            <li
                                                key={filter}
                                                className={`market-dropdown-item ${marketFilter === filter ? 'active' : ''}`}
                                                onClick={() => {
                                                    setMarketFilter(filter);
                                                    setDropdownOpen(false);
                                                }}
                                            >
                                                {filter}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Search Input */}
                            <div className="market-search-wrapper">
                                <i className="ri-search-line search-icon"></i>
                                <input
                                    type="text"
                                    placeholder="Search Coin"
                                    value={marketSearch}
                                    onChange={(e) => setMarketSearch(e.target.value)}
                                    className="market-search-input"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Market Table */}
                    <div className="market-table-container">
                        <table className="market-table">
                            <thead>
                                <tr>
                                    <th>Coin</th>
                                    <th>Est. APR</th>
                                    <th>Term</th>
                                    <th className="text-end">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMarketData.length > 0 ? (
                                    filteredMarketData.map((coin) => {
                                        const isExpanded = expandedRows.includes(coin.id);
                                        return (
                                            <React.Fragment key={coin.id}>
                                                {/* Main Row */}
                                                <tr
                                                    className={`market-main-row ${isExpanded ? 'expanded' : ''}`}
                                                    onClick={() => toggleRow(coin.id)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>
                                                        <div className="market-coin-cell d-flex align-items-center">
                                                            <img src={coin.icon} alt={coin.name} className="market-coin-icon me-3" />
                                                            <span className="market-coin-name">{coin.name}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="market-apr-value">{coin.apr}</span>
                                                    </td>
                                                    <td>
                                                        <span className="market-term-summary">{coin.termsText}</span>
                                                    </td>
                                                    <td className="text-end">
                                                        <button type="button" className="market-toggle-btn">
                                                            <i className={`ri-arrow-down-s-line market-chev-icon ${isExpanded ? 'open' : ''}`}></i>
                                                        </button>
                                                    </td>
                                                </tr>

                                                {/* Expanded Campaigns (Separate Rows) */}
                                                {isExpanded && coin.campaigns.map((c) => {
                                                    const currentTermLabel = selectedTerms[c.id] || c.defaultTerm;
                                                    const activeTermObj = c.terms.find(t => t.label === currentTermLabel) || c.terms[0];
                                                    const displayApr = activeTermObj.apr;

                                                    return (
                                                        <tr key={c.id} className="market-sub-row">
                                                            {/* Empty Coin Column */}
                                                            <td className="market-sub-empty-cell"></td>

                                                            {/* APR column with Tag and Trend icon */}
                                                            <td className="market-sub-apr-cell">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span className="market-sub-apr-value underlined">{displayApr}</span>
                                                                    {c.tags.map(tag => (
                                                                        <span key={tag} className={`market-sub-badge badge-${tag.toLowerCase().replace(/\s+/g, '-')}`}>
                                                                            {tag}
                                                                        </span>
                                                                    ))}
                                                                    {c.hasChart && <i className="ri-line-chart-line sub-row-chart-icon text-muted"></i>}
                                                                </div>
                                                            </td>

                                                            {/* Terms / Days column */}
                                                            <td className="market-sub-term-cell">
                                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                    {c.terms.map(t => {
                                                                        const isActive = currentTermLabel === t.label;
                                                                        return (
                                                                            <button
                                                                                key={t.label}
                                                                                type="button"
                                                                                className={`sub-term-pill ${isActive ? 'active' : ''}`}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation(); // don't collapse row
                                                                                    setSelectedTerms(prev => ({
                                                                                        ...prev,
                                                                                        [c.id]: t.label
                                                                                    }));
                                                                                }}
                                                                            >
                                                                                {t.label === "Flexible Term" ? "Flexible Term" : t.label}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </td>

                                                            {/* Subscribe column */}
                                                            <td className="text-end market-sub-action-cell">
                                                                <button
                                                                    type="button"
                                                                    className="market-subscribe-btn"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation(); // don't collapse row
                                                                        openSubscribeModal(coin.id, currentTermLabel);
                                                                    }}
                                                                >
                                                                    Subscribe
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="text-center py-4 text-muted">
                                            No coins found matching "{marketSearch}"
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Representation */}
                    <div className="market-pagination d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <span className="pagination-info">Showing 1-{filteredMarketData.length} of {filteredMarketData.length} products</span>
                        <div className="pagination-controls d-flex gap-2">
                            <button className="pagination-btn disabled" disabled><i className="ri-arrow-left-s-line"></i></button>
                            <button className="pagination-btn active">1</button>
                            <button className="pagination-btn disabled" disabled><i className="ri-arrow-right-s-line"></i></button>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="convert-page__faqSection mt-5 mb-5" aria-labelledby="simple-earn-faq-title">
                    <h2 id="simple-earn-faq-title" className="convert-page__faqTitle">
                        FAQ
                    </h2>
                    <ul className="convert-page__faqList">
                        {SIMPLE_EARN_FAQ.map((item, i) => (
                            <li key={i} className="convert-page__faqItem">
                                <button
                                    type="button"
                                    className="convert-page__faqBtn"
                                    onClick={() => toggleFaq(i)}
                                    aria-expanded={openFaq === i}
                                >
                                    <span>{item.question}</span>
                                    <i className={`ri-arrow-down-s-line convert-page__faqChev${openFaq === i ? " convert-page__faqChev--open" : ""}`} aria-hidden />
                                </button>
                                {openFaq === i ? (
                                    <div className="convert-page__faqPanel" role="region">
                                        {Array.isArray(item.answer) ? (
                                            item.answer.map((para, pIdx) => (
                                                <p key={pIdx} style={{ marginBottom: pIdx !== item.answer.length - 1 ? '16px' : '0', color: '#84888C', lineHeight: '1.6' }}>
                                                    {para}
                                                </p>
                                            ))
                                        ) : (
                                            <p style={{ margin: 0, color: '#84888C', lineHeight: '1.6' }}>{item.answer}</p>
                                        )}
                                    </div>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </section>

            </div>

            {showAboutModal && (
                <div className="about-modal-overlay" onClick={() => setShowAboutModal(false)}>
                    <div className="about-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="about-modal-close" onClick={() => setShowAboutModal(false)} aria-label="Close modal">
                            <i className="ri-close-line"></i>
                        </button>

                        <h3 className="about-modal-title">About Simple Earn</h3>

                        <div className="about-modal-tabs">
                            <button
                                type="button"
                                className={`about-modal-tab ${modalTab === 'Introduction' ? 'active' : ''}`}
                                onClick={() => setModalTab('Introduction')}
                            >
                                Introduction
                            </button>
                            <button
                                type="button"
                                className={`about-modal-tab ${modalTab === 'Proof of Reserves' ? 'active' : ''}`}
                                onClick={() => setModalTab('Proof of Reserves')}
                            >
                                Proof of Reserves
                            </button>
                        </div>

                        <div className="about-modal-body">
                            {modalTab === 'Introduction' ? (
                                <div>
                                    <div className="about-modal-subtabs">
                                        <button
                                            type="button"
                                            className={`about-modal-subtab ${introSubTab === 'Strengths' ? 'active' : ''}`}
                                            onClick={() => setIntroSubTab('Strengths')}
                                        >
                                            Strengths
                                        </button>
                                        <button
                                            type="button"
                                            className={`about-modal-subtab ${introSubTab === 'Product Risk' ? 'active' : ''}`}
                                            onClick={() => setIntroSubTab('Product Risk')}
                                        >
                                            Product Risk
                                        </button>
                                    </div>

                                    {introSubTab === 'Strengths' ? (
                                        <div className="about-modal-scrollable">
                                            <div className="about-modal-section">
                                                <h4>What is Simple Earn?</h4>
                                                <p>Through ARAB Global Simple Earn, you can profit from more than 800 cryptocurrencies and unleash the potential of your investment portfolios. Specifically, you can subscribe to flexible-term or fixed-term products to earn returns with crypto assets.</p>
                                            </div>
                                            <div className="about-modal-section">
                                                <h4>Easy Subscription</h4>
                                                <p>You can use your idle cryptocurrencies to subscribe to flexible-term products, which can be redeemed anytime, to earn daily rewards, or subscribe to flexible-term products for higher and more stable returns.</p>
                                            </div>
                                            <div className="about-modal-section">
                                                <h4>Auto-Earn</h4>
                                                <p>Idle cryptocurrencies in your Spot Account or Unified Account will be used to auto-subscribe to Simple Earn flexible-term products for flexible returns at 2:30 (UTC+0) and 15:30 (UTC+0) every day.</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="about-modal-scrollable">
                                            <div className="about-modal-section">
                                                <h4>Interest Rate Fluctuation</h4>
                                                <p>Simple Earn interest rates are subject to change based on market supply and demand. The estimated APR is updated hourly or daily, meaning actual returns may fluctuate over time.</p>
                                            </div>
                                            <div className="about-modal-section">
                                                <h4>Early Redemption Risk</h4>
                                                <p>While Simple Earn Flexible Term supports real-time redemption, early redemption of Simple Earn Fixed Term before maturity will forfeit all accrued rewards. The principal will be returned to your Spot Account or Trading Account within 24-48 hours after submitting the redemption request. Please note that early redemption is not supported within the last 1 hour before maturity.</p>
                                            </div>
                                            <div className="about-modal-section">
                                                <h4>Liquidity & Delays</h4>
                                                <p>During times of extreme market volatility or high redemption volume, redemption requests may experience temporary delays. Interest will continue to accrue during any such waiting periods.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="about-modal-scrollable">
                                    <p className="about-modal-intro-text">
                                        ARAB Global is the first mainstream platform committing to providing 100% capital reserve
                                    </p>

                                    <div className="about-modal-section">
                                        <h4>What is 100% Proof of Reserve?</h4>
                                        <p>A centralized trading platform manages a ledger for recording user assets in a database. As such, platforms face the challenge of proving that they have full custody of all users' assets in good condition.</p>
                                        <p>ARAB Global implemented the Merkle tree to resolve this issue, by storing the hash value of each user's account assets in the leaf nodes of the Merkle tree. Every user can audit the total amount of users' assets stored in the leaf nodes of the Merkle tree and verify whether his funds are included through a qualified third-party audit agency.</p>
                                        <p>If the assets stored in the Merkle tree are verified to be greater than or equal to 100%, it means the users' assets are kept in full on the platform, i.e. the platform provides 100% Proof of Reserves for the users' assets.</p>
                                    </div>

                                    <a
                                        href="/proof-of-reserves"
                                        className="about-modal-learn-more"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            navigate("/proof-of-reserves");
                                        }}
                                    >
                                        Learn More
                                    </a>
                                </div>
                            )}
                        </div>

                        <div className="about-modal-footer">
                            <button
                                type="button"
                                className="about-modal-guide-btn"
                                onClick={() => alertSuccessMessage("Opening User Guide...")}
                            >
                                User Guide
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSubscribeModal && subCoin && subCampaign && (
                <div className="sub-modal-overlay" onClick={() => setShowSubscribeModal(false)}>
                    <div className="sub-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="sub-modal-close" onClick={() => setShowSubscribeModal(false)} aria-label="Close modal">
                            <i className="ri-close-line"></i>
                        </button>

                        <div className="sub-modal-layout">
                            {/* Left Column */}
                            <div className="sub-modal-left">
                                <div className="sub-modal-coin-header">
                                    <img src={subCoin.icon} alt={subCoin.name} className="sub-modal-coin-icon" />
                                    <span className="sub-modal-coin-name">{subCoin.name}</span>
                                </div>

                                <div className="sub-modal-term-list">
                                    {subCampaign.terms.map((term) => {
                                        const isActive = subTerm === term.label;
                                        return (
                                            <div
                                                key={term.label}
                                                className={`sub-modal-term-card ${isActive ? 'active' : ''}`}
                                                onClick={() => setSubTerm(term.label)}
                                            >
                                                <div className="sub-modal-term-apr">{term.apr}</div>
                                                <div className="sub-modal-term-label">
                                                    {term.label === "Flexible Term" ? "Flexible" : `${term.label} Days`}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="sub-modal-amount-section">
                                    <div className="sub-modal-amount-header">
                                        <label className="sub-modal-label">Amount</label>
                                        <label className="sub-modal-auto-earn">
                                            <input
                                                type="checkbox"
                                                checked={subAutoEarn}
                                                onChange={() => {
                                                    if (!subAutoEarn) {
                                                        setShowAutoEarnModal(true);
                                                    } else {
                                                        setSubAutoEarn(false);
                                                    }
                                                }}
                                            />
                                            <span>Auto-Earn</span>
                                        </label>
                                    </div>

                                    <div className="sub-modal-input-wrapper">
                                        <input
                                            type="number"
                                            placeholder="Minimum 1"
                                            value={subAmount}
                                            onChange={(e) => setSubAmount(e.target.value)}
                                            min="1"
                                            className="sub-modal-amount-input"
                                        />
                                        <div className="sub-modal-input-right">
                                            <span className="sub-modal-token-name">{subCoin.name}</span>
                                            <div className="sub-modal-input-divider"></div>
                                            <span className="sub-modal-max-btn" onClick={() => setSubAmount("1000")}>Max</span>
                                        </div>
                                    </div>

                                    <div className="sub-modal-amount-footer">
                                        <span className="sub-modal-available-link" onClick={() => alertSuccessMessage("Add Funds clicked!")}>Available</span>
                                        <span className="sub-modal-available-val">
                                            0 {subCoin.name}
                                            <i className="ri-add-circle-fill sub-modal-add-icon" onClick={() => {
                                                const token = localStorage.getItem("token");
                                                if (token) {
                                                    setAddFundsOpen(true);
                                                } else {
                                                    navigate("/login");
                                                }
                                            }}></i>
                                        </span>
                                    </div>
                                </div>

                                <div className="sub-modal-apr-details">
                                    <h4 className="sub-modal-apr-title">Est. APR</h4>

                                    <div className="sub-modal-apr-box">
                                        {(() => {
                                            const activeTermObj = subCampaign.terms.find(t => t.label === subTerm) || subCampaign.terms[0];
                                            const fullAprNum = parseFloat(activeTermObj.apr);
                                            const isFlexible = subTerm === "Flexible Term";

                                            // Dynamic calculation
                                            let standardApr = activeTermObj.apr;
                                            let bonusApr = null;

                                            if (isFlexible && fullAprNum > 5.0) {
                                                standardApr = (fullAprNum - 5.0).toFixed(2) + "%";
                                                bonusApr = "5.00%";
                                            }

                                            return (
                                                <>
                                                    <div className="sub-modal-apr-row">
                                                        <span className="sub-modal-apr-row-label">
                                                            Standard APR <i className="ri-line-chart-line"></i>
                                                        </span>
                                                        <span className="sub-modal-apr-row-val">{standardApr}</span>
                                                    </div>

                                                    {bonusApr && (
                                                        <>
                                                            <div className="sub-modal-apr-bonus-label">Bonus</div>
                                                            <div className="sub-modal-apr-row bonus-row">
                                                                <span className="sub-modal-apr-row-label">
                                                                    <i className="ri-checkbox-circle-fill green-tick"></i> Tier 1: 0-500
                                                                </span>
                                                                <span className="sub-modal-apr-row-val green-text">{bonusApr}</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="sub-modal-right">
                                <div className="sub-modal-return-section">
                                    <h4 className="sub-modal-return-title">Est. Daily Return</h4>

                                    <div className="sub-modal-return-box">
                                        {(() => {
                                            const activeTermObj = subCampaign.terms.find(t => t.label === subTerm) || subCampaign.terms[0];
                                            const aprVal = parseFloat(activeTermObj.apr) / 100;
                                            const amt = parseFloat(subAmount) || 0;
                                            const activeDailyReward = ((amt * aprVal) / 365).toFixed(6);
                                            const bonusDailyReward = amt > 0 ? ((amt * 0.01) / 365).toFixed(6) : "0";

                                            return (
                                                <>
                                                    <div className="sub-modal-return-row">
                                                        <img src={subCoin.icon} alt={subCoin.name} className="sub-modal-mini-icon" />
                                                        <span className="sub-modal-return-amount green-text">{activeDailyReward} {subCoin.name}</span>
                                                    </div>

                                                    <div className="sub-modal-return-row">
                                                        <img src="/images/staking_img/agce_icon.svg" alt="GT" className="sub-modal-mini-icon" />
                                                        <span className="sub-modal-return-amount blue-text">{bonusDailyReward} GT</span>
                                                        <span className="sub-modal-bonus-badge">Bonus</span>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>

                                <div className="sub-modal-rules-tabs">
                                    <button
                                        type="button"
                                        className={`sub-modal-rules-tab ${subRulesTab === 'Trading Rules' ? 'active' : ''}`}
                                        onClick={() => setSubRulesTab('Trading Rules')}
                                    >
                                        Trading Rules
                                    </button>
                                    <button
                                        type="button"
                                        className={`sub-modal-rules-tab ${subRulesTab === 'Product Rules' ? 'active' : ''}`}
                                        onClick={() => setSubRulesTab('Product Rules')}
                                    >
                                        Product Rules
                                    </button>
                                </div>

                                <div className="sub-modal-rules-content">
                                    {subRulesTab === 'Trading Rules' ? (
                                        <div className="sub-modal-timeline">
                                            <div className="sub-modal-timeline-line"></div>

                                            <div className="sub-modal-timeline-step">
                                                <div className="sub-modal-timeline-bullet active"></div>
                                                <span className="sub-modal-step-label">Time Subscribed</span>
                                                <span className="sub-modal-step-val">Now</span>
                                            </div>

                                            <div className="sub-modal-timeline-step">
                                                <div className="sub-modal-timeline-bullet"></div>
                                                <span className="sub-modal-step-label">Interest Starts</span>
                                                <span className="sub-modal-step-val">
                                                    {new Date(Date.now() + 86400000).toISOString().split('T')[0]} 16:00
                                                </span>
                                            </div>

                                            <div className="sub-modal-timeline-step">
                                                <div className="sub-modal-timeline-bullet"></div>
                                                <span className="sub-modal-step-label">Interest Received</span>
                                                <span className="sub-modal-step-val">
                                                    {new Date(Date.now() + 172800000).toISOString().split('T')[0]} 17:00
                                                </span>
                                            </div>

                                            <div className="sub-modal-timeline-step">
                                                <div className="sub-modal-timeline-bullet"></div>
                                                <span className="sub-modal-step-label">Time Redeemed</span>
                                                <span className="sub-modal-step-val">
                                                    {subTerm === "Flexible Term"
                                                        ? "Immediately"
                                                        : `Maturity (${new Date(Date.now() + (parseInt(subTerm) || 7) * 86400000).toISOString().split('T')[0]} 16:00)`
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="sub-modal-product-rules">
                                            <p><strong>Subscription Limit:</strong> Min 1 {subCoin.name}, Max 1,000,000 {subCoin.name}.</p>
                                            <p><strong>Redemption Policy:</strong> Flexible terms support instant redemptions. Fixed terms are locked for the duration; early redemptions will forfeit all accrued rewards.</p>
                                            <p><strong>Dynamic Rates:</strong> APR updates periodically based on market utilization and liquidity pool status.</p>
                                        </div>
                                    )}
                                </div>

                                <div className="sub-modal-footer">
                                    <button
                                        type="button"
                                        className={`sub-modal-submit-btn ${parseFloat(subAmount) >= 1 ? 'active' : 'disabled'}`}
                                        disabled={!(parseFloat(subAmount) >= 1)}
                                        onClick={() => {
                                            alertSuccessMessage(`Successfully subscribed to ${subCoin.name} ${subTerm}!`);
                                            setShowSubscribeModal(false);
                                        }}
                                    >
                                        Subscription
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showAutoEarnModal && (
                <div className="auto-earn-modal-overlay" onClick={() => setShowAutoEarnModal(false)}>
                    <div className="auto-earn-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="auto-earn-modal-header">
                            <div className="auto-earn-modal-header-left" onClick={() => setShowAutoEarnModal(false)}>
                                <i className="ri-arrow-left-s-line"></i>
                                <span>Enable Auto-Earn</span>
                            </div>
                            <button className="auto-earn-modal-close" onClick={() => setShowAutoEarnModal(false)} aria-label="Close modal">
                                &times;
                            </button>
                        </div>

                        <div className="auto-earn-modal-body">
                            <div className="auto-earn-illustration">
                                <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    {/* Wallet back */}
                                    <rect x="25" y="45" width="70" height="46" rx="8" fill="#1e2329" />
                                    {/* Wallet front flap */}
                                    <rect x="25" y="53" width="70" height="38" rx="8" fill="#ffffff" stroke="#1e2329" strokeWidth="3" />
                                    {/* Wallet clasp */}
                                    <rect x="85" y="64" width="15" height="16" rx="4" fill="#ffffff" stroke="#1e2329" strokeWidth="3" />
                                    <circle cx="91" cy="72" r="3" fill="#1e2329" />
                                    {/* Coin with Bitcoin symbol */}
                                    <circle cx="50" cy="35" r="18" fill="#ffffff" stroke="#1e2329" strokeWidth="3" />
                                    <path d="M47 27H51.5C53.5 27 55 28 55 29.5C55 31 54 32 52.5 32.5C54.5 33 55.5 34.5 55.5 36C55.5 38 53.5 39 51.5 39H47V27ZM50 31H51.5C52.5 31 53 30.5 53 30C53 29.5 52.5 29 51.5 29H50V31ZM50 37H51.5C52.5 37 53 36.5 53 36C53 35.5 52.5 35 51.5 35H50V37Z" fill="#1e2329" />
                                    {/* Green bubble decoration */}
                                    <circle cx="75" cy="32" r="10" fill="#9ae6b4" stroke="#1e2329" strokeWidth="2.5" />
                                    {/* Blue circular arrows badge */}
                                    <circle cx="40" cy="85" r="14" fill="#0066ff" />
                                    <path d="M37 81C38 80 39.5 79.5 41 79.5C43.5 79.5 45.5 81 46.5 83M43 89C42 90 40.5 90.5 39 90.5C36.5 90.5 34.5 89 33.5 87" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
                                    <path d="M46.5 80V83H43.5M33.5 90V87H36.5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>

                            <p className="auto-earn-description">
                                Once enabled, the coin balance in your Trading Account will be automatically transferred to Flexible Term at 02:30 (UTC) and 15:30 (UTC) every day (the actual amount may vary given various margin adjustment factors) and can be redeemed anytime. You will receive the interest at the next full hour after your assets are successfully borrowed.
                            </p>

                            <div className="auto-earn-warning-box">
                                <i className="ri-error-warning-fill auto-earn-warning-icon"></i>
                                <p className="auto-earn-warning-text">
                                    Note: After enabling Auto-Earn, all transferable assets of the coin will be transferred to Simple Earn. If you hold derivatives positions, liquidation could be triggered in Trading Account when the MMR is too low. Please closely monitor your positions and manage your risks accordingly.
                                </p>
                            </div>
                        </div>

                        <div className="auto-earn-modal-footer">
                            <button
                                type="button"
                                className="auto-earn-cancel-btn"
                                onClick={() => setShowAutoEarnModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="auto-earn-confirm-btn"
                                onClick={() => {
                                    setSubAutoEarn(true);
                                    setShowAutoEarnModal(false);
                                    alertSuccessMessage("Auto-Earn successfully enabled!");
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showInviteModal && (
                <div className="invite-modal-overlay" onClick={() => setShowInviteModal(false)}>
                    <div className="invite-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="invite-modal-close" onClick={() => setShowInviteModal(false)} aria-label="Close modal">
                            &times;
                        </button>

                        <h3 className="invite-modal-title">Invite Friends</h3>

                        <div className="invite-modal-layout">
                            {/* Left Column - Card Graphic */}
                            <div className="invite-modal-left">
                                <div className="invite-card-media">

                                    <div className="invite-card-brand-name">ARAB Global</div>
                                    <div className="invite-card-tagline">Simple Earn Yields</div>
                                    <div className="invite-card-apr-badge">Up to 100% Est. APR</div>
                                </div>

                                <div className="invite-card-bottom">
                                    <div className="invite-card-info">
                                        <div className="invite-card-code-label">Referral Code: VFAQVA1FCA</div>
                                        <div className="invite-card-reward-label">Sign Up to Win <span className="highlight-blue">10,000+ USDT</span> in Rewards</div>
                                    </div>

                                    <div className="invite-card-qr">
                                        <svg height="160" width="160" viewBox="0 0 37 37" className="deposit_qr_img"><path fill="#FFFFFF" d="M0,0 h37v37H0z" shapeRendering="crispEdges"></path><path fill="#000000" d="M4 4h7v1H4zM14 4h2v1H14zM17 4h1v1H17zM20 4h1v1H20zM23 4h2v1H23zM26,4 h7v1H26zM4 5h1v1H4zM10 5h1v1H10zM13 5h1v1H13zM17 5h3v1H17zM21 5h1v1H21zM26 5h1v1H26zM32,5 h1v1H32zM4 6h1v1H4zM6 6h3v1H6zM10 6h1v1H10zM12 6h1v1H12zM15 6h2v1H15zM19 6h1v1H19zM22 6h3v1H22zM26 6h1v1H26zM28 6h3v1H28zM32,6 h1v1H32zM4 7h1v1H4zM6 7h3v1H6zM10 7h1v1H10zM12 7h1v1H12zM14 7h1v1H14zM17 7h6v1H17zM26 7h1v1H26zM28 7h3v1H28zM32,7 h1v1H32zM4 8h1v1H4zM6 8h3v1H6zM10 8h1v1H10zM12 8h3v1H12zM17 8h2v1H17zM20 8h1v1H20zM23 8h2v1H23zM26 8h1v1H26zM28 8h3v1H28zM32,8 h1v1H32zM4 9h1v1H4zM10 9h1v1H10zM12 9h2v1H12zM16 9h1v1H16zM19 9h1v1H19zM26 9h1v1H26zM32,9 h1v1H32zM4 10h7v1H4zM12 10h1v1H12zM14 10h1v1H14zM16 10h1v1H16zM18 10h1v1H18zM20 10h1v1H20zM22 10h1v1H22zM24 10h1v1H24zM26,10 h7v1H26zM12 11h4v1H12zM18 11h1v1H18zM22 11h1v1H22zM4 12h1v1H4zM6 12h5v1H6zM13 12h1v1H13zM17 12h5v1H17zM24 12h1v1H24zM26 12h5v1H26zM5 13h5v1H5zM11 13h1v1H11zM13 13h3v1H13zM18 13h1v1H18zM20 13h1v1H20zM22 13h2v1H22zM25 13h4v1H25zM32,13 h1v1H32zM4 14h1v1H4zM9 14h7v1H9zM19 14h1v1H19zM21 14h1v1H21zM24 14h1v1H24zM28 14h2v1H28zM4 15h3v1H4zM12 15h2v1H12zM15 15h1v1H15zM19 15h1v1H19zM22 15h5v1H22zM28 15h2v1H28zM4 16h3v1H4zM8 16h1v1H8zM10 16h1v1H10zM13 16h5v1H13zM20 16h3v1H20zM24 16h2v1H24zM27 16h1v1H27zM29 16h3v1H29zM5 17h3v1H5zM9 17h1v1H9zM13 17h4v1H13zM18 17h1v1H18zM23 17h1v1H23zM26 17h1v1H26zM28 17h1v1H28zM30 17h1v1H30zM32,17 h1v1H32zM5 18h1v1H5zM8 18h6v1H8zM15 18h1v1H15zM17 18h1v1H17zM19 18h2v1H19zM24 18h1v1H24zM27 18h3v1H27zM31 18h1v1H31zM4 19h1v1H4zM6 19h3v1H6zM11 19h1v1H11zM14 19h3v1H14zM19 19h2v1H19zM22 19h4v1H22zM27 19h1v1H27zM32,19 h1v1H32zM4 20h1v1H4zM8 20h5v1H8zM14 20h1v1H14zM17 20h3v1H17zM21 20h1v1H21zM25 20h1v1H25zM29 20h3v1H29zM4 21h2v1H4zM7 21h1v1H7zM12 21h3v1H12zM16 21h1v1H16zM18 21h1v1H18zM20 21h5v1H20zM26 21h3v1H26zM31,21 h2v1H31zM4 22h1v1H4zM6 22h1v1H6zM8 22h1v1H8zM10 22h1v1H10zM12 22h2v1H12zM15 22h1v1H15zM17 22h5v1H17zM25 22h1v1H25zM29 22h2v1H29zM4 23h1v1H4zM7 23h1v1H7zM14 23h2v1H14zM20 23h1v1H20zM22 23h1v1H22zM24 23h1v1H24zM26 23h3v1H26zM31,23 h2v1H31zM4 24h1v1H4zM7 24h1v1H7zM10 24h1v1H10zM14 24h1v1H14zM16 24h6v1H16zM23 24h9v1H23zM12 25h2v1H12zM16 25h2v1H16zM22 25h1v1H22zM24 25h1v1H24zM28 25h2v1H28zM31,25 h2v1H31zM4 26h7v1H4zM16 26h2v1H16zM19 26h1v1H19zM21 26h1v1H21zM23 26h2v1H23zM26 26h1v1H26zM28 26h1v1H28zM4 27h1v1H4zM10 27h1v1H10zM12 27h2v1H12zM15 27h2v1H15zM19 27h2v1H19zM23 27h2v1H23zM28 27h2v1H28zM31,27 h2v1H31zM4 28h1v1H4zM6 28h3v1H6zM10 28h1v1H10zM12 28h1v1H12zM15 28h1v1H15zM19 28h3v1H19zM24,28 h9v1H24zM4 29h1v1H4zM6 29h3v1H6zM10 29h1v1H10zM12 29h1v1H12zM15 29h1v1H15zM20 29h1v1H20zM24 29h1v1H24zM27 29h1v1H27zM31,29 h2v1H31zM4 30h1v1H4zM6 30h3v1H6zM10 30h1v1H10zM12 30h1v1H12zM14 30h3v1H14zM18 30h5v1H18zM24 30h3v1H24zM28 30h1v1H28zM31 30h1v1H31zM4 31h1v1H4zM10 31h1v1H10zM13 31h6v1H13zM22 31h5v1H22zM31 31h1v1H31zM4 32h7v1H4zM12 32h2v1H12zM18 32h6v1H18zM25 32h2v1H25zM28 32h3v1H28z" shapeRendering="crispEdges"></path></svg>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column - Referral Settings */}
                            <div className="invite-modal-right">
                                <div className="invite-field-group">
                                    <label className="invite-field-label">Referral Code</label>
                                    <div className="invite-code-input-container">
                                        <span className="invite-code-text">VFAQVA1FCA</span>
                                        <button
                                            type="button"
                                            className="invite-code-copy-btn"
                                            onClick={() => {
                                                navigator.clipboard.writeText("VFAQVA1FCA");
                                                alertSuccessMessage("Referral Code copied!");
                                            }}
                                            aria-label="Copy Referral Code"
                                        >
                                            <i className="ri-file-copy-line"></i>
                                        </button>
                                    </div>
                                </div>

                                <div className="invite-field-group">
                                    <label className="invite-field-label">Customize Your Text</label>
                                    <div className="invite-custom-textbox" onClick={() => {
                                        const text = "Join me to subscribe to USDT in ARAB Global Simple Earn. The current APR is up to 6.80%. Click the link and earn together: https://www.arabglobal.com/simple-earn?ref=VFAQVA1FCA&ref_type=121";
                                        navigator.clipboard.writeText(text);
                                        alertSuccessMessage("Invitation text copied!");
                                    }}>
                                        Join me to subscribe to USDT in ARAB Global Simple Earn. The current APR is up to 6.80%. Click the link and earn together: https://www.arabglobal.com/simple-earn?ref=VFAQVA1FCA&ref_type=121
                                    </div>
                                </div>

                                <div className="invite-share-grid">
                                    {/* Copy */}
                                    <div className="invite-share-item" onClick={() => {
                                        navigator.clipboard.writeText("https://www.arabglobal.com/simple-earn?ref=VFAQVA1FCA&ref_type=121");
                                        alertSuccessMessage("Link copied!");
                                    }}>
                                        <div className="invite-share-circle grey-circle">
                                            <i className="ri-file-copy-line"></i>
                                        </div>
                                        <span className="invite-share-name">Copy</span>
                                    </div>

                                    {/* Download */}
                                    <div className="invite-share-item" onClick={() => alertSuccessMessage("Downloading poster image...")}>
                                        <div className="invite-share-circle grey-circle">
                                            <i className="ri-download-line"></i>
                                        </div>
                                        <span className="invite-share-name">Download</span>
                                    </div>

                                    {/* Telegram */}
                                    <div className="invite-share-item" onClick={() => alertSuccessMessage("Sharing to Telegram...")}>
                                        <div className="invite-share-circle telegram-circle">
                                            <i className="ri-telegram-fill"></i>
                                        </div>
                                        <span className="invite-share-name">Telegram</span>
                                    </div>

                                    {/* WhatsApp */}
                                    <div className="invite-share-item" onClick={() => alertSuccessMessage("Sharing to WhatsApp...")}>
                                        <div className="invite-share-circle whatsapp-circle">
                                            <i className="ri-whatsapp-fill"></i>
                                        </div>
                                        <span className="invite-share-name">WhatsApp</span>
                                    </div>

                                    {/* X */}
                                    <div className="invite-share-item" onClick={() => alertSuccessMessage("Sharing to X...")}>
                                        <div className="invite-share-circle x-circle">
                                            <i className="ri-twitter-x-fill"></i>
                                        </div>
                                        <span className="invite-share-name">X</span>
                                    </div>

                                    {/* Facebook */}
                                    <div className="invite-share-item" onClick={() => alertSuccessMessage("Sharing to Facebook...")}>
                                        <div className="invite-share-circle facebook-circle">
                                            <i className="ri-facebook-circle-fill"></i>
                                        </div>
                                        <span className="invite-share-name">Facebook</span>
                                    </div>

                                    {/* LinkedIn */}
                                    <div className="invite-share-item" onClick={() => alertSuccessMessage("Sharing to LinkedIn...")}>
                                        <div className="invite-share-circle linkedin-circle">
                                            <i className="ri-linkedin-box-fill"></i>
                                        </div>
                                        <span className="invite-share-name">LinkedIn</span>
                                    </div>

                                    {/* Line */}
                                    <div className="invite-share-item" onClick={() => alertSuccessMessage("Sharing to Line...")}>
                                        <div className="invite-share-circle line-circle">
                                            <i className="ri-line-fill"></i>
                                        </div>
                                        <span className="invite-share-name">Line</span>
                                    </div>

                                    {/* Email */}
                                    <div className="invite-share-item" onClick={() => alertSuccessMessage("Opening Mail client...")}>
                                        <div className="invite-share-circle email-circle">
                                            <i className="ri-mail-fill"></i>
                                        </div>
                                        <span className="invite-share-name">Email</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <AddFundsModal
                open={addFundsOpen}
                onClose={() => setAddFundsOpen(false)}
                onTransferClick={openWalletTransfer}
            />
        </div>
    );
};

const SimpleEarnWrapper = () => {
    return (
        <FuturesTradeTransferProvider>
            <SimpleEarn />
        </FuturesTradeTransferProvider>
    );
};

export default SimpleEarnWrapper;

